import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  DataList,
  EmptyState,
  ErrorState,
  Nota,
  PageHeader,
  Pagination,
  SearchInput,
  Skeleton,
} from '../../../ui';

import ModalFiltrosLogs, {
  FILTROS_LOGS_VAZIOS,
  FiltrosLogs,
  contarFiltrosLogsAtivos,
  paraFiltrosDaApi,
} from '../../../components/modal/filtros-logs/ModalFiltrosLogs';
import ModalDetalheLog from '../../../components/modal/detalhe-log/ModalDetalheLog';

import { TI } from '../routes';
import {
  exportarLogGeral,
  listarLogGeral,
  LogGeralItem,
  obterOpcoesLogGeral,
  OpcoesLogGeral,
} from '../../../api/log-geral/logGeralApi';
import { formatarDataHora } from '../../../utils/data';
import { descreverOrigem, descreverQuem, rotuloResultado } from '../../../utils/logGeralRegras';

/**
 * `/ti/logs` — **Logs do Sistema** (at-85, item 66). TI e ADMIN.
 *
 * Tudo o que acontece no SAAC: quem, quando, o quê, em qual registro, de onde e se deu certo —
 * inclusive o que foi recusado. Molde: a tela de mesmo nome do SOAC (ADR 0062 de lá).
 *
 * 🔴 **Falha de carga NUNCA vira "nenhum registro"**: numa tela de auditoria, lista vazia por
 * erro se lê como "ninguém fez nada", a conclusão mais cara possível (regra da at-60).
 *
 * ⚠️ Paginação numerada: linhas novas entram no topo o tempo todo, então a página N pode
 * repetir ou pular um registro entre um clique e outro. A nota da tela diz isso, e o filtro de
 * período é o jeito de fixar o recorte.
 */

const POR_PAGINA = 20;

type EstadoLista = 'carregando' | 'ok' | 'erro';

const LogsPage: React.FC = () => {
  const voltar = useVoltar(TI());

  const [itens, setItens] = useState<LogGeralItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [filtros, setFiltros] = useState<FiltrosLogs>(FILTROS_LOGS_VAZIOS);
  const [estado, setEstado] = useState<EstadoLista>('carregando');
  const [erro, setErro] = useState('');
  const [opcoes, setOpcoes] = useState<OpcoesLogGeral | null>(null);

  const [filtrosAberto, setFiltrosAberto] = useState(false);
  const [detalheId, setDetalheId] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);
  const [chaveBusca, setChaveBusca] = useState(0);

  const filtrosAtivos = contarFiltrosLogsAtivos(filtros);
  const filtrosDaApi = useMemo(() => paraFiltrosDaApi(filtros, busca), [filtros, busca]);

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = await listarLogGeral(filtrosDaApi, pagina, POR_PAGINA);
      setItens(resposta.itens ?? []);
      setTotal(resposta.total ?? 0);
      setEstado('ok');
    } catch (falha) {
      // A lista é DESCARTADA no erro: manter a anterior faria registro velho passar por atual.
      setItens([]);
      setErro(falha instanceof Error ? falha.message : '');
      setEstado('erro');
    }
  }, [filtrosDaApi, pagina]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // As opções só enriquecem o modal de filtros (módulos, ações vistas): falhar aqui não pode
  // derrubar a tela — o modal cai nos campos livres.
  useEffect(() => {
    obterOpcoesLogGeral()
      .then(setOpcoes)
      .catch(() => setOpcoes(null));
  }, []);

  const trocarBusca = (termo: string) => {
    setBusca(termo);
    setPagina(1);
  };

  const aplicarFiltros = (novos: FiltrosLogs) => {
    setFiltros(novos);
    setPagina(1);
    setFiltrosAberto(false);
  };

  const limparTudo = () => {
    setFiltros(FILTROS_LOGS_VAZIOS);
    setBusca('');
    setPagina(1);
    setChaveBusca((c) => c + 1);
  };

  const exportar = async () => {
    setExportando(true);
    try {
      const { blob, nomeDoArquivo, cortado } = await exportarLogGeral(filtrosDaApi);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeDoArquivo;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      if (cortado) {
        toast.warn('A exportação parou em 50.000 linhas. Escolha um período menor para levar o restante.');
      } else {
        toast.success('Log exportado.');
      }
    } catch (falha) {
      toast.error(falha instanceof Error ? falha.message : 'Não foi possível exportar o log.');
    } finally {
      setExportando(false);
    }
  };

  const colunas = [
    {
      id: 'oque',
      rotulo: 'O que',
      principal: true,
      celula: (l: LogGeralItem) => l.resumo,
    },
    {
      id: 'quando',
      rotulo: 'Quando',
      secundaria: true,
      celula: (l: LogGeralItem) => formatarDataHora(l.createdAt),
    },
    {
      id: 'quem',
      rotulo: 'Quem',
      secundaria: true,
      celula: (l: LogGeralItem) => descreverQuem(l),
    },
    {
      id: 'registro',
      rotulo: 'Registro',
      ocultarNoCard: true,
      celula: (l: LogGeralItem) => <span className="tw-text-xs tw-text-texto-suave">{l.registro}</span>,
    },
    {
      id: 'origem',
      rotulo: 'Origem',
      ocultarNoCard: true,
      celula: (l: LogGeralItem) => (
        <span className="tw-break-all tw-font-mono tw-text-[11px] tw-text-texto-suave">{descreverOrigem(l)}</span>
      ),
    },
    {
      id: 'resultado',
      rotulo: 'Resultado',
      celula: (l: LogGeralItem) => {
        const r = rotuloResultado(l);
        return <Chip variante={r.variante}>{r.texto}</Chip>;
      },
    },
  ];

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={6} altura="tw-h-14" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar o log do sistema."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (itens.length === 0) {
      // Vazio com recorte ≠ vazio sem recorte: o primeiro pede para afrouxar o filtro.
      if (busca || filtrosAtivos > 0) {
        return (
          <EmptyState
            icone="busca"
            titulo="Nenhum registro com esse recorte"
            descricao={
              filtros.de || filtros.entidadeId
                ? 'Nada corresponde à busca e aos filtros escolhidos.'
                : 'Nada corresponde à busca e aos filtros nos últimos 7 dias. Para ir mais longe, escolha um período nos Filtros.'
            }
            acao={
              <Botao variante="secundario" onClick={limparTudo}>
                Limpar busca e filtros
              </Botao>
            }
          />
        );
      }
      return (
        <EmptyState
          icone="sem-dados"
          titulo="Nenhum registro nos últimos 7 dias"
          descricao="Toda ação que altera alguma coisa no sistema aparece aqui. Para ver um período mais antigo, use os Filtros."
        />
      );
    }

    return (
      <>
        <DataList
          itens={itens}
          colunas={colunas}
          chave={(l) => l.id}
          rotuloAcoes="Abrir"
          rotuloAcessivel="Registros do log do sistema"
          acoes={(l) => (
            <Botao
              variante="secundario"
              tamanho="sm"
              onClick={() => setDetalheId(l.id)}
              className="tw-w-full md:tw-w-auto"
            >
              Abrir
            </Botao>
          )}
        />

        <div className="tw-mt-4">
          <Pagination pagina={pagina} totalItens={total} porPagina={POR_PAGINA} aoTrocar={setPagina} />
        </div>
      </>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="Logs do Sistema"
          sobretitulo="Administração (TI)"
          aoVoltar={voltar}
          acaoDireita={
            <Botao
              variante="secundario"
              tamanho="md"
              iconeEsquerda="documento"
              carregando={exportando}
              disabled={exportando || estado !== 'ok'}
              aria-label="Exportar CSV"
              onClick={() => void exportar()}
            >
              <span className="tw-hidden lg:tw-inline">Exportar CSV</span>
            </Botao>
          }
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Nota>
                Toda ação que <strong>altera alguma coisa</strong> no SAAC fica registrada aqui, com
                quem fez, quando, de onde e se deu certo — inclusive as tentativas recusadas. Também
                entram as rotinas automáticas, os envios ao SGA, as sincronizações, a abertura de PDF
                de contrato e as exportações. Consultas comuns não entram. Senhas e tokens nunca são
                gravados; CPF, RG e telefone aparecem mascarados. Ninguém edita nem apaga este log.
              </Nota>

              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <SearchInput
                  key={chaveBusca}
                  valorInicial={busca}
                  placeholder="Buscar no resumo (ex.: vistoria, contrato)"
                  rotuloAcessivel="Buscar no log"
                  className="tw-min-w-0 tw-flex-1 tw-basis-64"
                  aoBuscar={trocarBusca}
                />

                <Botao
                  variante="secundario"
                  tamanho="md"
                  iconeEsquerda="filtro"
                  aria-pressed={filtrosAtivos > 0}
                  onClick={() => setFiltrosAberto(true)}
                >
                  {filtrosAtivos > 0 ? `Filtros (${filtrosAtivos})` : 'Filtros'}
                </Botao>
              </div>

              <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-texto-suave">
                {filtros.de || filtros.entidadeId
                  ? `${total} registro(s) no recorte escolhido.`
                  : `${estado === 'ok' ? `${total} registro(s) nos ` : 'Mostrando os '}últimos 7 dias.`}{' '}
                Do mais recente para o mais antigo; registros novos entram no topo, então trocar de
                página pode repetir ou pular uma linha.
              </p>

              {conteudo()}
            </div>
          </div>
        </div>
      </div>

      <ModalFiltrosLogs
        aberto={filtrosAberto}
        valor={filtros}
        opcoes={opcoes}
        onAplicar={aplicarFiltros}
        onFechar={() => setFiltrosAberto(false)}
      />

      <ModalDetalheLog id={detalheId} aoFechar={() => setDetalheId(null)} />
    </ShellSaac>
  );
};

export default LogsPage;
